import React from 'react';
import PropTypes from 'prop-types';
import { LuTriangleAlert } from 'react-icons/lu';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useSmoText from '../SMOLeadReports/smoText';
import { ChartCard, useChartName, useChartTheme } from '../SMOLeadReports/parts/chartKit';
import { ALERT_TONES, HeroCard } from '../SMOLeadReports/parts/OperationalOverview';
import { rateTone } from '../SMOLeadReports/parts/tones';
import useRegionText from './regionText';

/** Body of the region's "Operasyonel Genel Bakış": metric cards, alerts and two charts. */
const RegionSummary = ({ summary, regionColor }) => {
  const { label: smo, locale } = useSmoText();
  const { label } = useRegionText();
  const chart = useChartTheme();
  const chartName = useChartName();

  return (
    <div className="tw-space-y-4">
      {/* Hero Metric Cards */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3 xl:tw-grid-cols-5">
        <HeroCard tone="blue" value={summary.total} title={smo('overview.totalVisits')} sub={smo('overview.homeSite', { home: summary.homeCount, site: summary.siteCount })} />
        <HeroCard tone={rateTone(summary.qualityRate, 90, 70)} value={`%${summary.qualityRate}`} title={smo('overview.qualityRate')} sub={smo('overview.issueVisits', { count: summary.issueCount })} />
        <HeroCard tone={rateTone(summary.slaRate, 80, 50)} value={`%${summary.slaRate}`} title={smo('overview.slaRate')} sub={smo('overview.overdueVisits', { count: summary.overdueCount })} />
        <HeroCard tone={rateTone(summary.docRate, 80, 50)} value={`%${summary.docRate}`} title={smo('overview.docRate')} sub={smo('overview.docSub')} />
        <HeroCard tone="purple" value={summary.totalKm > 0 ? summary.totalKm.toLocaleString(locale) : '—'} title={smo('overview.totalKm')} sub={smo('overview.kmSub')} />
      </div>

      {/* Alerts */}
      {summary.alerts.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {summary.alerts.map((alert) => (
            <span key={alert.metric} className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-text-xs tw-font-semibold ${ALERT_TONES[alert.type]}`}>
              <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4" />
              {smo(`alerts.${alert.metric}`, alert.vars)}
            </span>
          ))}
        </div>
      )}

      {/* Charts */}
      {(summary.monthlyTrend.length > 1 || summary.workerChart.length > 0) && (
        <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
          {summary.monthlyTrend.length > 1 && (
            <ChartCard title={label('summary.monthlyTrend')}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={summary.monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="month" tick={chart.tick} />
                  <YAxis allowDecimals={false} tick={chart.tick} />
                  <Tooltip {...chart.tooltip} />
                  <Legend wrapperStyle={chart.legend} />
                  <Line type="monotone" dataKey="Ev" name={chartName('Ev')} stroke="#4299e1" strokeWidth={2} />
                  <Line type="monotone" dataKey="Hastane" name={chartName('Hastane')} stroke="#9f7aea" strokeWidth={2} />
                  <Line type="monotone" dataKey="Sorun" name={chartName('Sorun')} stroke="#e53e3e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {summary.workerChart.length > 0 && (
            <ChartCard title={label('summary.workerChart')}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summary.workerChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="name" tick={chart.tick} angle={-25} textAnchor="end" height={70} />
                  <YAxis allowDecimals={false} tick={chart.tick} />
                  <Tooltip labelFormatter={(name, payload) => payload?.[0]?.payload?.fullName || name} {...chart.tooltip} />
                  <Legend wrapperStyle={chart.legend} />
                  <Bar dataKey="Vizit" name={chartName('Vizit')} fill={regionColor} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Sorun" name={chartName('Sorun')} fill="#e53e3e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="tw-mt-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('summary.workerChartHelp')}</p>
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
};

RegionSummary.propTypes = {
  summary: PropTypes.object.isRequired,
  regionColor: PropTypes.string,
};

export default RegionSummary;
